import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { takeOver } from './api';
import { takeOverMessage } from './kewixFormat';
import { TransportItem } from './types';

/**
 * Confirm step for "Take over". Taking over only removes the call from Kewix's
 * transport queue: it assigns nobody, and Give back undoes it.
 */
export const KewixTakeOverDialog = ({
  item,
  onClose,
  onDone,
}: {
  item: TransportItem | null;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [busy, setBusy] = useState(false);
  const notify = useNotify();

  const confirm = async () => {
    if (!item) return;
    setBusy(true);
    try {
      const res = await takeOver(item.id);
      const msg = takeOverMessage(res);
      notify(msg.text, { type: msg.ok ? 'success' : 'warning' });
      onDone();
      onClose();
    } catch {
      notify("Couldn't take this over. Nothing changed for Kewix — try again.", { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!item} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Take over this transport from Kewix?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" paragraph>
          Kewix will remove this call from their transport queue and won&apos;t dispatch it. It
          doesn&apos;t make you the owner.
        </Typography>
        <Typography variant="body2" paragraph>
          Kewix may already be calling the transport company. If in doubt, call them.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You can give it back to Kewix from here afterwards.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={() => void confirm()}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Take over
        </Button>
      </DialogActions>
    </Dialog>
  );
};
